/**
 * Merge thread messages after send or realtime sync without dropping optimistic rows.
 */

function messageTimeMs(msg) {
  const t = Date.parse(msg?.sentAt ?? "");
  return Number.isFinite(t) ? t : 0;
}

function isEphemeralAgentRow(message) {
  const id = String(message?.id ?? "");
  return message?.ephemeral === true || id.startsWith("local-") || id.startsWith("rt-");
}

/**
 * @param {Array<Record<string, unknown>>} prev
 * @param {Array<Record<string, unknown>>} incoming
 * @returns {Array<Record<string, unknown>>}
 */
export function mergeThreadMessages(prev, incoming) {
  const prevList = Array.isArray(prev) ? prev : [];
  const incomingList = Array.isArray(incoming) ? incoming : [];
  const byId = new Map();

  for (const m of incomingList) {
    if (!m?.id) continue;
    byId.set(String(m.id), { ...m });
  }

  for (const m of prevList) {
    if (!m?.id) continue;
    const id = String(m.id);
    if (byId.has(id)) {
      const inc = byId.get(id);
      const prevText = String(m.text ?? "").trim();
      const incText = String(inc.text ?? "").trim();
      if (prevText && !incText) {
        byId.set(id, { ...inc, text: m.text });
      }
      continue;
    }
    if (isEphemeralAgentRow(m)) {
      byId.set(id, { ...m });
    }
  }

  return [...byId.values()].sort((a, b) => {
    const ta = messageTimeMs(a);
    const tb = messageTimeMs(b);
    if (ta !== tb) return ta - tb;
    return String(a.id).localeCompare(String(b.id));
  });
}

/**
 * @param {Array<Record<string, unknown>>} prev
 * @param {Record<string, unknown>} msg
 * @returns {Array<Record<string, unknown>>}
 */
export function upsertThreadMessage(prev, msg) {
  if (!msg?.id) return prev;
  const id = String(msg.id);
  const list = Array.isArray(prev) ? [...prev] : [];
  const idx = list.findIndex((m) => String(m.id) === id);
  if (idx >= 0) {
    const existing = list[idx];
    const nextText = String(msg.text ?? "").trim() || String(existing.text ?? "").trim();
    list[idx] = { ...existing, ...msg, text: nextText || existing.text || msg.text };
    return list;
  }
  return [...list, msg];
}
